import { useState, useEffect } from 'react';
import { adminApi } from './AdminContext';
import { 
  ArrowLeft, Plus, Search, Phone, Edit2, Trash2, 
  X, Building2, MapPin, DollarSign, Mail, User
} from 'lucide-react';

const SUPPLIER_TYPES = [
  'Aluminium', 'Glass', 'Hardware', 'Paint', 
  'Cleaning Materials', 'Flooring', 'General Materials'
];

const EMIRATES = ['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'RAK', 'Fujairah', 'UAQ'];
const PRICE_LEVELS = ['Low', 'Medium', 'High'];

const SuppliersModule = ({ onBack, initialAction }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showForm, setShowForm] = useState(initialAction === 'add');
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  const [formData, setFormData] = useState({
    company_name: '',
    contact_person: '',
    phone: '',
    email: '',
    supplier_type: '',
    location: '',
    price_level: 'Medium',
    notes: '',
    tags: []
  });

  useEffect(() => {
    loadSuppliers();
  }, [searchQuery, filterType]);

  const loadSuppliers = async () => {
    setLoading(true);
    try {
      const filters = {};
      if (searchQuery) filters.search = searchQuery;
      if (filterType) filters.supplier_type = filterType;
      const data = await adminApi.getSuppliers(filters);
      setSuppliers(data);
    } catch (error) {
      console.error('Failed to load suppliers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSupplier) {
        await adminApi.updateSupplier(editingSupplier.id, formData);
      } else {
        await adminApi.createSupplier(formData);
      }

      setShowForm(false);
      setEditingSupplier(null);
      resetForm();
      loadSuppliers();
    } catch (error) {
      console.error('Failed to save supplier:', error);
    }
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      company_name: supplier.company_name,
      contact_person: supplier.contact_person,
      phone: supplier.phone,
      email: supplier.email || '',
      supplier_type: supplier.supplier_type,
      location: supplier.location,
      price_level: supplier.price_level,
      notes: supplier.notes || '',
      tags: supplier.tags || []
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this supplier?')) {
      await adminApi.deleteSupplier(id);
      loadSuppliers();
      if (selectedSupplier?.id === id) setSelectedSupplier(null);
    }
  };

  const resetForm = () => {
    setFormData({
      company_name: '',
      contact_person: '',
      phone: '',
      email: '',
      supplier_type: '',
      location: '',
      price_level: 'Medium',
      notes: '',
      tags: []
    });
  };

  const getPriceLevelColor = (level) => {
    switch (level) {
      case 'Low': return 'bg-green-500/20 text-green-400';
      case 'Medium': return 'bg-yellow-500/20 text-yellow-400';
      case 'High': return 'bg-red-500/20 text-red-400';
      default: return 'bg-slate-500/20 text-slate-400';
    }
  };

  // Supplier Card Component
  const SupplierCard = ({ supplier }) => (
    <div 
      className="bg-[#0A0F1C] border border-white/10 rounded-lg p-4 cursor-pointer hover:border-white/20"
      onClick={() => setSelectedSupplier(supplier)}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-white font-semibold">{supplier.company_name}</h3>
          <p className="text-slate-400 text-sm">{supplier.supplier_type}</p>
        </div>
        <span className={`px-2 py-1 rounded text-xs font-medium ${getPriceLevelColor(supplier.price_level)}`}>
          {supplier.price_level}
        </span>
      </div>
      
      <div className="flex items-center gap-4 text-sm mb-3">
        <span className="flex items-center gap-1 text-slate-300">
          <User size={14} className="text-slate-400" />
          {supplier.contact_person}
        </span>
        <span className="flex items-center gap-1 text-slate-300">
          <MapPin size={14} className="text-slate-400" />
          {supplier.location}
        </span>
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-white/10">
        <a 
          href={`https://wa.me/${supplier.phone.replace(/[^0-9]/g, '')}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-[#25D366] text-sm"
          onClick={(e) => e.stopPropagation()}
        >
          <Phone size={14} />
          {supplier.phone}
        </a>
        <div className="flex gap-2">
          <button 
            onClick={(e) => { e.stopPropagation(); handleEdit(supplier); }}
            className="p-2 text-slate-400 hover:text-white"
          >
            <Edit2 size={16} />
          </button>
          <button 
            onClick={(e) => { e.stopPropagation(); handleDelete(supplier.id); }}
            className="p-2 text-slate-400 hover:text-red-400"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );

  // Detail View
  if (selectedSupplier) {
    return (
      <div className="min-h-screen bg-[#060B14]">
        <header className="bg-[#0A0F1C] border-b border-white/10 sticky top-0 z-40">
          <div className="flex items-center gap-3 px-4 py-3">
            <button onClick={() => setSelectedSupplier(null)} className="text-white p-2">
              <ArrowLeft size={24} />
            </button>
            <h1 className="text-lg font-bold text-white">Supplier Details</h1>
          </div>
        </header>

        <main className="p-4 pb-24">
          {/* Profile Header */}
          <div className="bg-[#0A0F1C] border border-white/10 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center">
                <Building2 size={32} className="text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{selectedSupplier.company_name}</h2>
                <p className="text-slate-400">{selectedSupplier.supplier_type}</p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-3 text-slate-300">
                <User size={18} className="text-slate-400" />
                <span>{selectedSupplier.contact_person}</span>
              </div>
              <a 
                href={`tel:${selectedSupplier.phone}`}
                className="flex items-center gap-3 text-slate-300"
              >
                <Phone size={18} className="text-[#25D366]" />
                <span>{selectedSupplier.phone}</span>
              </a>
              {selectedSupplier.email && (
                <a 
                  href={`mailto:${selectedSupplier.email}`}
                  className="flex items-center gap-3 text-slate-300"
                >
                  <Mail size={18} className="text-blue-400" />
                  <span>{selectedSupplier.email}</span>
                </a>
              )}
              <div className="flex items-center gap-3 text-slate-300">
                <MapPin size={18} className="text-orange-400" />
                <span>{selectedSupplier.location}</span>
              </div>
              <div className="flex items-center gap-3">
                <DollarSign size={18} className="text-green-400" />
                <span className={`px-2 py-1 rounded text-sm ${getPriceLevelColor(selectedSupplier.price_level)}`}>
                  {selectedSupplier.price_level} Price Level
                </span>
              </div>
            </div>

            {selectedSupplier.notes && (
              <p className="mt-4 text-slate-400 text-sm border-t border-white/10 pt-4">
                {selectedSupplier.notes}
              </p>
            )}
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <a 
              href={`https://wa.me/${selectedSupplier.phone.replace(/[^0-9]/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 py-3 bg-[#25D366] text-white font-semibold rounded-lg"
            >
              <Phone size={18} />
              WhatsApp
            </a>
            <a 
              href={`tel:${selectedSupplier.phone}`}
              className="flex items-center justify-center gap-2 py-3 bg-blue-500 text-white font-semibold rounded-lg"
            >
              <Phone size={18} />
              Call
            </a>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button 
              onClick={() => handleEdit(selectedSupplier)}
              className="flex-1 py-3 bg-purple-500 text-white font-semibold rounded-lg"
            >
              Edit Supplier
            </button>
            <button 
              onClick={() => handleDelete(selectedSupplier.id)}
              className="px-4 py-3 bg-red-500/20 text-red-400 font-semibold rounded-lg"
            >
              <Trash2 size={20} />
            </button>
          </div>
        </main>
      </div>
    );
  }

  // Form View
  if (showForm) {
    return (
      <div className="min-h-screen bg-[#060B14]">
        <header className="bg-[#0A0F1C] border-b border-white/10 sticky top-0 z-40">
          <div className="flex items-center gap-3 px-4 py-3">
            <button onClick={() => { setShowForm(false); setEditingSupplier(null); resetForm(); }} className="text-white p-2">
              <X size={24} />
            </button>
            <h1 className="text-lg font-bold text-white">
              {editingSupplier ? 'Edit Supplier' : 'Add Supplier'}
            </h1>
          </div>
        </header>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-slate-400 text-sm mb-2">Company Name *</label>
            <input
              type="text"
              value={formData.company_name}
              onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              className="w-full px-4 py-3 bg-[#0A0F1C] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#25D366]"
              required
            />
          </div>

          <div>
            <label className="block text-slate-400 text-sm mb-2">Contact Person *</label>
            <input
              type="text"
              value={formData.contact_person}
              onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
              className="w-full px-4 py-3 bg-[#0A0F1C] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#25D366]"
              required
            />
          </div>

          <div>
            <label className="block text-slate-400 text-sm mb-2">Phone *</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-4 py-3 bg-[#0A0F1C] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#25D366]"
              placeholder="+971..."
              required
            />
          </div>

          <div>
            <label className="block text-slate-400 text-sm mb-2">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-3 bg-[#0A0F1C] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#25D366]"
              placeholder="email@example.com"
            />
          </div>

          <div>
            <label className="block text-slate-400 text-sm mb-2">Supplier Type *</label>
            <select
              value={formData.supplier_type}
              onChange={(e) => setFormData({ ...formData, supplier_type: e.target.value })}
              className="w-full px-4 py-3 bg-[#0A0F1C] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#25D366]"
              required
            >
              <option value="">Select type</option>
              {SUPPLIER_TYPES.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-slate-400 text-sm mb-2">Location (Emirate) *</label>
            <select
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              className="w-full px-4 py-3 bg-[#0A0F1C] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#25D366]"
              required
            >
              <option value="">Select emirate</option>
              {EMIRATES.map((emirate) => (
                <option key={emirate} value={emirate}>{emirate}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-slate-400 text-sm mb-2">Price Level</label>
            <div className="flex gap-2">
              {PRICE_LEVELS.map((level) => (
                <button
                  key={level}
                  type="button"
                  onClick={() => setFormData({ ...formData, price_level: level })}
                  className={`flex-1 py-2 rounded-lg border ${
                    formData.price_level === level
                      ? level === 'Low' 
                        ? 'bg-green-500 border-green-500 text-white'
                        : level === 'Medium'
                        ? 'bg-yellow-500 border-yellow-500 text-white'
                        : 'bg-red-500 border-red-500 text-white'
                      : 'border-white/10 text-slate-400'
                  }`}
                >
                  {level}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-slate-400 text-sm mb-2">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-4 py-3 bg-[#0A0F1C] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#25D366] min-h-[100px]"
              placeholder="Additional notes..."
            />
          </div>

          <button
            type="submit"
            className="w-full py-3 bg-[#25D366] text-white font-semibold rounded-lg"
          >
            {editingSupplier ? 'Update Supplier' : 'Add Supplier'}
          </button>
        </form>
      </div>
    );
  }

  // List View
  return (
    <div className="min-h-screen bg-[#060B14]">
      <header className="bg-[#0A0F1C] border-b border-white/10 sticky top-0 z-40">
        <div className="flex items-center gap-3 px-4 py-3">
          <button onClick={onBack} className="text-white p-2">
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-lg font-bold text-white">Suppliers</h1>
          <button 
            onClick={() => setShowForm(true)}
            className="ml-auto p-2 bg-[#25D366] rounded-lg"
          >
            <Plus size={20} className="text-white" />
          </button>
        </div>

        {/* Search & Filter */}
        <div className="px-4 pb-3 space-y-2">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name or phone..."
              className="w-full pl-10 pr-4 py-2 bg-[#060B14] border border-white/10 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-[#25D366]"
            />
          </div>
          
          <div className="flex gap-2 overflow-x-auto pb-1">
            <button
              onClick={() => setFilterType('')}
              className={`px-3 py-1 rounded-full text-sm whitespace-nowrap ${
                !filterType ? 'bg-[#25D366] text-white' : 'bg-white/10 text-slate-400'
              }`}
            >
              All
            </button>
            {SUPPLIER_TYPES.map((type) => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={`px-3 py-1 rounded-full text-sm whitespace-nowrap ${
                  filterType === type ? 'bg-[#25D366] text-white' : 'bg-white/10 text-slate-400'
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="p-4 pb-24">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-[#25D366] border-t-transparent rounded-full"></div>
          </div>
        ) : suppliers.length === 0 ? (
          <div className="text-center py-12">
            <Building2 size={48} className="text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No suppliers found</p>
            <button 
              onClick={() => setShowForm(true)}
              className="mt-4 px-4 py-2 bg-[#25D366] text-white rounded-lg"
            >
              Add First Supplier
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {suppliers.map((supplier) => (
              <SupplierCard key={supplier.id} supplier={supplier} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default SuppliersModule;
